/**
 * PilotageTjmController — page `/pilotage-tjm.html` (EPIC-031 / 31.5,
 * spec §16.C). Calculateur TJM 3 niveaux (minimum / cible / optimal).
 *
 * Charge `GET /pilotage/tjm` au `connect()` :
 *  - hydrate les champs du formulaire depuis `profile` (revenu net
 *    mensuel souhaité, ACRE/VL/TMI, jours ouvrés/congés/non facturés,
 *    charges fixes mensuelles) ;
 *  - rend les 3 cartes de TJM (minimum/target/optimal) et la
 *    décomposition de la cible affichée par défaut ;
 *  - reflète le régime réel du ME et les taux appliqués dans le champ
 *    « Régime », le bandeau d'hypothèses et les libellés de la
 *    décomposition à partir du bloc `assumptions` renvoyé par l'API
 *    (`_renderAssumptions`) — plus de valeurs codées en dur côté HTML.
 *
 * Sauvegarde automatique (décision PO « auto-save assumé ») :
 *  - chaque modification (`input` / `change`) déclenche un recalcul +
 *    upsert debouncé (300 ms) via `scheduleRecalc()` →
 *    `PUT /pilotage/tjm`. Pas de bouton Enregistrer/Réinitialiser :
 *    un indicateur (`saveStatus`) affiche l'état (« Enregistrement… » →
 *    « ✓ Enregistré »).
 *
 * Sélection de la décomposition : chaque carte TJM porte un bouton
 * « Voir la décomposition » (`showBreakdown`, tier via params Stimulus).
 * Le bloc de décomposition adopte le fond de la carte sélectionnée
 * (`_applyTierSelection` + `TIER_BG`).
 *
 * Convention CLAUDE.md respectée : pas de HTML inline en JS — tout
 * passe par `textContent` / `createElement` ou par bascule de
 * classes/attributs déjà présents dans le HTML statique.
 */

import BaseController from "./base_controller.js";
import { api } from "/js/services/api.js";

const EURO = new Intl.NumberFormat("fr-FR", {
  minimumFractionDigits: 0,
  maximumFractionDigits: 0,
});

const EURO_2 = new Intl.NumberFormat("fr-FR", {
  minimumFractionDigits: 2,
  maximumFractionDigits: 2,
});

const RECALC_DEBOUNCE_MS = 300;

const TIER_LABEL = {
  minimum: "TJM minimum",
  target: "TJM cible",
  optimal: "TJM optimal",
};

/**
 * Fond appliqué au bloc de décomposition pour le faire correspondre
 * visuellement à la carte du palier sélectionné (cf. cartes TJM dans
 * pilotage-tjm.html). `optimal` revient au fond blanc par défaut de la
 * card.
 */
const TIER_BG = {
  minimum: "var(--surface)",
  target: "var(--accent-soft)",
  optimal: "",
};

/**
 * Mapping des `key-param` des inputs de charges (cf. pilotage-tjm.html)
 * vers la structure JSONB `fixed_charges_monthly`. Les `custom_*` sont
 * agrégés dans le tableau `custom` à l'envoi.
 */
const CHARGE_KEYS = {
  notamicro: "notamicro",
  rcpro: "rcpro",
  sante: "sante",
  custom_0: { custom: 0, label: "Banque pro" },
  custom_1: { custom: 1, label: "Téléphone / internet pro" },
  custom_2: { custom: 2, label: "Loyer / coworking" },
  custom_3: { custom: 3, label: "Autres charges" },
};

export default class PilotageTjmController extends BaseController {
  static targets = [
    "saveStatus",
    "incomeInput",
    "categoryDisplay",
    "acreDisplay",
    "vlDisplay",
    "tmiSelect",
    "businessDaysInput",
    "vacationDaysInput",
    "nonBillableInput",
    "billableDaysDisplay",
    "chargeInput",
    "totalChargesDisplay",
    "tierBlock",
    "tierButton",
    "breakdownSection",
    "tjmMinValue",
    "tjmTargetValue",
    "tjmTargetSubtitle",
    "tjmOptimalValue",
    "tjmOptimalSubtitle",
    "breakdownTitle",
    "breakdownCa",
    "breakdownFormulaRow",
    "breakdownFormula",
    "breakdownFormulaResult",
    "breakdownUrssafLabel",
    "breakdownUrssafValue",
    "breakdownIrLabel",
    "breakdownIrValue",
    "breakdownChargesLabel",
    "breakdownChargesValue",
    "breakdownNetAnnual",
    "breakdownNetMonthly",
    "ceilingAlert",
    "ceilingAlertText",
    "vatAlert",
    "vatAlertText",
    "assumptionsList",
    "realSection",
    "realNote",
    "realData",
    "realCaValue",
    "realCaDelta",
    "realTargetCaValue",
    "realTargetCaDelta",
  ];

  /** @type {object | null} */
  profile = null;

  /** @type {object | null} */
  lastResults = null;

  /** @type {object | null} */
  assumptions = null;

  /** @type {object | null} */
  realActivity = null;

  /** @type {string} */
  visibleTier = "target";

  /** @type {number | null} */
  _recalcTimer = null;

  async connect() {
    try {
      const payload = await api.getPilotageTjm();
      this._hydrate(payload);
    } catch (error) {
      console.warn("[pilotage-tjm] hydration failed", error);
      this.showError("Impossible de charger votre profil TJM.");
    }
  }

  disconnect() {
    if (this._recalcTimer !== null) {
      window.clearTimeout(this._recalcTimer);
      this._recalcTimer = null;
    }
  }

  _hydrate(payload) {
    if (!payload || typeof payload !== "object") return;

    this.profile = payload.profile ?? null;
    this.lastResults = payload.results ?? null;
    this.assumptions = payload.assumptions ?? null;
    this.realActivity = payload.realActivity ?? null;
    if (this.profile === null || this.lastResults === null) return;

    this._populateFormFromProfile(this.profile);
    this._renderTjmTiers(this.lastResults);
    this._renderBreakdown(this.visibleTier, this.lastResults);
    this._renderBillableDays();
    this._renderTotalCharges();
    this._renderAssumptions(this.assumptions);
    this._renderRealActivity(this.realActivity, this.lastResults);
    this._applyTierSelection(this.visibleTier);
  }

  _populateFormFromProfile(profile) {
    if (this.hasIncomeInputTarget) {
      this.incomeInputTarget.value = this._formatEditableNumber(
        Number(profile.desiredMonthlyIncome) || 0,
        0,
      );
    }
    if (this.hasAcreDisplayTarget) {
      this.acreDisplayTarget.textContent = profile.acreApplied
        ? "Oui — taux URSSAF réduit"
        : "Non";
    }
    if (this.hasVlDisplayTarget) {
      this.vlDisplayTarget.textContent = profile.irVlOpted
        ? "Oui — taux libératoire"
        : "Non — barème";
    }
    if (this.hasTmiSelectTarget && profile.estimatedTmi !== null) {
      const tmiPercent = Math.round(Number(profile.estimatedTmi) * 100);
      const option = Array.from(this.tmiSelectTarget.options).find(
        (opt) => opt.text.startsWith(`${tmiPercent} %`),
      );
      if (option) {
        this.tmiSelectTarget.value = option.value || option.text;
        option.selected = true;
      }
    }
    if (this.hasBusinessDaysInputTarget) {
      this.businessDaysInputTarget.value = String(profile.businessDaysPerYear);
    }
    if (this.hasVacationDaysInputTarget) {
      this.vacationDaysInputTarget.value = String(profile.vacationDays);
    }
    if (this.hasNonBillableInputTarget) {
      const effective = Math.max(0, profile.businessDaysPerYear - profile.vacationDays);
      const nonBillableDays = Math.round(effective * Number(profile.unbilledRatio));
      this.nonBillableInputTarget.value = String(nonBillableDays);
    }
    this._populateChargesInputs(profile.fixedChargesMonthly ?? {});
  }

  _populateChargesInputs(fixedCharges) {
    if (!this.hasChargeInputTarget) return;

    this.chargeInputTargets.forEach((input) => {
      const key = input.dataset.pilotageTjmKeyParam ?? "";
      const mapping = CHARGE_KEYS[key];
      if (mapping === undefined) return;

      if (typeof mapping === "string") {
        const value = fixedCharges[mapping];
        input.value = value === null || value === undefined
          ? ""
          : this._formatEditableNumber(Number(value), 2);
        return;
      }

      const customs = Array.isArray(fixedCharges.custom) ? fixedCharges.custom : [];
      const entry = customs[mapping.custom];
      input.value = entry && entry.amount !== undefined
        ? this._formatEditableNumber(Number(entry.amount), 2)
        : "";
    });
  }

  _renderTjmTiers(results) {
    if (this.hasTjmMinValueTarget) {
      this.tjmMinValueTarget.textContent = EURO.format(results.minimum.tjm);
    }
    if (this.hasTjmTargetValueTarget) {
      this.tjmTargetValueTarget.textContent = EURO.format(results.target.tjm);
    }
    if (this.hasTjmTargetSubtitleTarget) {
      const monthly = EURO.format(Math.round(results.target.revenuNetMensuel));
      this.tjmTargetSubtitleTarget.textContent = `Pour ${monthly} € net / mois (cible saisie).`;
    }
    if (this.hasTjmOptimalValueTarget) {
      this.tjmOptimalValueTarget.textContent = EURO.format(results.optimal.tjm);
    }
    if (this.hasTjmOptimalSubtitleTarget) {
      const monthly = EURO.format(Math.round(results.optimal.revenuNetMensuel));
      this.tjmOptimalSubtitleTarget.textContent
        = `Pour ${monthly} € net / mois (cible × 150 % — épargne, formation, retraite).`;
    }
  }

  _renderBreakdown(tier, results) {
    const data = results[tier];
    if (!data) return;

    if (this.hasBreakdownTitleTarget) {
      this.breakdownTitleTarget.textContent
        = `Décomposition · ${TIER_LABEL[tier]} ${EURO.format(data.tjm)} €`;
    }
    if (this.hasBreakdownCaTarget) {
      this.breakdownCaTarget.textContent = `${EURO.format(data.caAnnuel)} €`;
    }
    const billableDays = this._currentBillableDays();
    if (this.hasBreakdownFormulaTarget) {
      this.breakdownFormulaTarget.replaceChildren();
      const mono = document.createElement("span");
      mono.className = "fs-11 text-subtle";
      mono.style.fontFamily = "var(--mono)";
      mono.style.marginRight = "8px";
      mono.textContent = `${EURO.format(data.tjm)} €`;
      this.breakdownFormulaTarget.appendChild(mono);
      this.breakdownFormulaTarget.appendChild(document.createTextNode(
        ` × ${billableDays} jours facturables`,
      ));
    }
    if (this.hasBreakdownFormulaResultTarget) {
      this.breakdownFormulaResultTarget.textContent = `= ${EURO.format(data.caAnnuel)} €`;
    }
    if (this.hasBreakdownUrssafValueTarget) {
      this.breakdownUrssafValueTarget.textContent = `− ${EURO.format(data.urssafAnnuel)} €`;
    }
    if (this.hasBreakdownIrValueTarget) {
      this.breakdownIrValueTarget.textContent = `− ${EURO.format(data.irAnnuel)} €`;
    }
    if (this.hasBreakdownChargesValueTarget) {
      this.breakdownChargesValueTarget.textContent
        = `− ${EURO.format(data.chargesFixesAnnuelles)} €`;
    }
    if (this.hasBreakdownNetAnnualTarget) {
      this.breakdownNetAnnualTarget.textContent = `${EURO.format(data.revenuNetAnnuel)} €`;
    }
    if (this.hasBreakdownNetMonthlyTarget) {
      this.breakdownNetMonthlyTarget.replaceChildren();
      this.breakdownNetMonthlyTarget.appendChild(document.createTextNode(
        `${EURO.format(Math.round(data.revenuNetMensuel))} € / mois `,
      ));
      const subtle = document.createElement("span");
      subtle.className = "text-subtle";
      subtle.textContent = `(${TIER_LABEL[tier]})`;
      this.breakdownNetMonthlyTarget.appendChild(subtle);
    }

    this._renderThresholdAlerts(data.caAnnuel);
  }

  /**
   * Affiche, sous la décomposition du palier affiché, une alerte
   * (warning) si le CA visé dépasse le plafond micro-entreprise — au ton
   * mesuré : la sortie du régime n'intervient qu'après 2 années civiles
   * consécutives — et une info si le CA dépasse le seuil de franchise en
   * base de TVA. Seuils résolus par catégorie côté backend (`assumptions`).
   */
  _renderThresholdAlerts(caAnnuel) {
    const a = this.assumptions;
    const ca = Number(caAnnuel) || 0;
    const ceiling = a ? Number(a.microCeiling) || 0 : 0;
    const vatBase = a ? Number(a.vatBaseThreshold) || 0 : 0;

    if (this.hasCeilingAlertTarget) {
      const over = ceiling > 0 && ca > ceiling;
      this.ceilingAlertTarget.hidden = !over;
      if (over && this.hasCeilingAlertTextTarget) {
        this._renderParts(this.ceilingAlertTextTarget, [
          "Le CA annuel visé (",
          { b: `${EURO.format(ca)} €` },
          ") dépasse le plafond micro-entreprise (",
          { b: `${EURO.format(ceiling)} €` },
          "). La sortie du régime n'intervient qu'après ",
          { b: "2 années civiles consécutives" },
          " au-dessus du plafond — voyez-le comme un repère, pas un couperet.",
        ]);
      }
    }

    if (this.hasVatAlertTarget) {
      const over = vatBase > 0 && ca > vatBase;
      this.vatAlertTarget.hidden = !over;
      if (over && this.hasVatAlertTextTarget) {
        this._renderParts(this.vatAlertTextTarget, [
          "Au-delà de ",
          { b: `${EURO.format(vatBase)} €` },
          " de CA, vous sortez de la franchise en base de TVA : vous devrez facturer la TVA à vos clients.",
        ]);
      }
    }
  }

  /**
   * Bloc « Simulation vs réalité » : masqué tant qu'aucune donnée n'est
   * disponible ; sinon affiche soit une note (micro < 12 mois, pas
   * d'historique exploitable) soit la comparaison CA réel 12 mois
   * glissants vs CA cible. Données fournies par `realActivity` (API).
   */
  _renderRealActivity(real, results) {
    if (!this.hasRealSectionTarget) return;

    if (!real || typeof real !== "object") {
      this.realSectionTarget.hidden = true;

      return;
    }
    this.realSectionTarget.hidden = false;

    const available = Boolean(real.available);
    if (this.hasRealNoteTarget) this.realNoteTarget.hidden = available;
    if (this.hasRealDataTarget) this.realDataTarget.hidden = !available;
    if (!available || results === null) return;

    const realCa = Number(real.rollingCaHt) || 0;
    const targetCa = Number(results.target?.caAnnuel) || 0;

    if (this.hasRealCaValueTarget) {
      this.realCaValueTarget.textContent = `${EURO.format(realCa)} €`;
    }
    if (this.hasRealTargetCaValueTarget) {
      this.realTargetCaValueTarget.textContent = `${EURO.format(targetCa)} €`;
    }
    if (this.hasRealCaDeltaTarget) {
      this.realCaDeltaTarget.textContent = this._realCaDeltaLabel(realCa, targetCa);
    }
    if (this.hasRealTargetCaDeltaTarget) {
      const remaining = targetCa - realCa;
      this.realTargetCaDeltaTarget.textContent = remaining > 0
        ? `+ ${EURO.format(remaining)} € à réaliser`
        : "Objectif atteint";
    }
  }

  _realCaDeltaLabel(realCa, targetCa) {
    if (targetCa <= 0) return "—";
    const ratio = realCa / targetCa;
    const pct = this._pct(Math.abs(1 - ratio), 1);
    if (realCa < targetCa) return `${pct} % sous l'objectif`;
    if (realCa > targetCa) return `${pct} % au-dessus de l'objectif`;

    return "Au niveau de l'objectif";
  }

  /**
   * Reflète le régime réel du ME (BIC vente / BIC services / BNC) et les
   * taux appliqués dans le champ « Régime », le bandeau d'hypothèses et
   * les libellés de la décomposition — d'après le bloc `assumptions`
   * renvoyé par l'API (plus de valeurs codées en dur côté HTML).
   */
  _renderAssumptions(a) {
    if (!a || typeof a !== "object") return;

    if (this.hasCategoryDisplayTarget && typeof a.categoryLabel === "string") {
      this.categoryDisplayTarget.textContent = a.categoryLabel;
    }
    this._renderAssumptionsList(a);
    this._renderBreakdownLabels(a);
  }

  _renderAssumptionsList(a) {
    if (!this.hasAssumptionsListTarget) return;

    const business = Number(this.businessDaysInputTarget?.value ?? 0) || 0;
    const vacation = Number(this.vacationDaysInputTarget?.value ?? 0) || 0;
    const nonBillable = Number(this.nonBillableInputTarget?.value ?? 0) || 0;
    const billable = this._currentBillableDays();
    const urssafPct = this._pct(a.urssafRateApplied, 1);

    const items = [
      ["Régime ", { b: a.categoryLabel ?? "—" }, ` · abattement forfaitaire ${this._pct(a.allowanceRate, 0)} %`],
      a.acreApplied
        ? [{ b: "ACRE actif" }, ` · taux URSSAF réduit appliqué (${urssafPct} %)`]
        : [`Sans ACRE · taux URSSAF ${urssafPct} % appliqué`],
      ["Plafond ", { b: "75 % PASS" }, " non franchi sur la projection · pas de dégressivité"],
      a.irVlOpted
        ? ["IR au ", { b: "versement libératoire" }, ` (${this._pct(a.vlRate, 1)} % du CA)`]
        : ["IR au ", { b: "barème progressif" }, ` (TMI ${this._pct(a.estimatedTmi, 0)} %) · option versement libératoire désactivée`],
      [{ b: `${billable} jours facturables / an` }, ` = ${business} ouvrés − ${vacation} congés − ${nonBillable} non facturés`],
      ["Charges fixes pré-remplies depuis votre profil · modifiables ci-contre"],
    ];

    this.assumptionsListTarget.replaceChildren(
      ...items.map((parts) => this._liFromParts(parts)),
    );
  }

  _renderBreakdownLabels(a) {
    if (this.hasBreakdownUrssafLabelTarget) {
      const urssafPct = this._pct(a.urssafRateApplied, 1);
      this.breakdownUrssafLabelTarget.textContent = a.acreApplied
        ? `− Cotisations URSSAF (taux ACRE ${urssafPct} %)`
        : `− Cotisations URSSAF (taux ${urssafPct} %)`;
    }
    if (this.hasBreakdownIrLabelTarget) {
      this.breakdownIrLabelTarget.textContent = a.irVlOpted
        ? `− IR (versement libératoire ${this._pct(a.vlRate, 1)} %)`
        : `− IR (barème · TMI ${this._pct(a.estimatedTmi, 0)} % après abattement ${this._pct(a.allowanceRate, 0)} %)`;
    }
  }

  _liFromParts(parts) {
    const li = document.createElement("li");
    this._renderParts(li, parts);

    return li;
  }

  /**
   * Remplit un élément à partir d'une liste de fragments : une chaîne
   * devient un nœud texte, un objet `{ b }` un `<strong>`. Évite la
   * construction de HTML inline (convention CLAUDE.md).
   */
  _renderParts(el, parts) {
    el.replaceChildren();
    parts.forEach((part) => {
      if (typeof part === "string") {
        el.appendChild(document.createTextNode(part));
      } else {
        const strong = document.createElement("strong");
        strong.textContent = part.b;
        el.appendChild(strong);
      }
    });
  }

  _pct(rate, decimals) {
    const value = (Number(rate) || 0) * 100;

    return new Intl.NumberFormat("fr-FR", {
      minimumFractionDigits: 0,
      maximumFractionDigits: decimals,
    }).format(value);
  }

  _renderBillableDays() {
    if (!this.hasBillableDaysDisplayTarget) return;

    const billable = this._currentBillableDays();
    this.billableDaysDisplayTarget.textContent = `${billable} j / an`;
  }

  _renderTotalCharges() {
    if (!this.hasTotalChargesDisplayTarget || !this.hasChargeInputTarget) return;

    let total = 0;
    this.chargeInputTargets.forEach((input) => {
      const value = this._parseLooseNumber(input.value);
      if (Number.isFinite(value)) total += value;
    });
    this.totalChargesDisplayTarget.textContent = `${EURO_2.format(total)} € / mois`;

    if (this.hasBreakdownChargesLabelTarget) {
      this.breakdownChargesLabelTarget.textContent
        = `− Charges fixes annuelles (${EURO_2.format(total)} € × 12)`;
    }
  }

  _currentBillableDays() {
    const business = Number(this.businessDaysInputTarget?.value ?? 0) || 0;
    const vacation = Number(this.vacationDaysInputTarget?.value ?? 0) || 0;
    const nonBillable = Number(this.nonBillableInputTarget?.value ?? 0) || 0;
    const billable = business - vacation - nonBillable;

    return billable > 0 ? billable : 0;
  }

  scheduleRecalc() {
    this._renderBillableDays();
    this._renderTotalCharges();
    this._setSaveStatus("Enregistrement…");

    if (this._recalcTimer !== null) {
      window.clearTimeout(this._recalcTimer);
    }
    this._recalcTimer = window.setTimeout(() => {
      this._recalcTimer = null;
      void this._submit();
    }, RECALC_DEBOUNCE_MS);
  }

  /**
   * Affiche la décomposition du palier dont la carte a été cliquée
   * (bouton « Voir la décomposition »). Le tier provient des params
   * Stimulus (`data-pilotage-tjm-tier-param`).
   */
  showBreakdown(event) {
    const tier = event?.params?.tier;
    if (typeof tier !== "string" || TIER_LABEL[tier] === undefined) return;

    this.visibleTier = tier;
    if (this.lastResults !== null) {
      this._renderBreakdown(tier, this.lastResults);
    }
    this._applyTierSelection(tier);
  }

  /**
   * Met le bloc de décomposition au fond du palier sélectionné, met en
   * évidence la carte active et peint les boutons (blanc au repos, accent
   * quand actif — la bordure reste visible quel que soit le fond de la
   * carte). La ligne « formule » repasse en blanc quand le fond du bloc
   * devient `surface` (palier minimum) pour ne pas se confondre avec lui.
   */
  _applyTierSelection(tier) {
    const sectionBg = TIER_BG[tier] ?? "";
    if (this.hasBreakdownSectionTarget) {
      this.breakdownSectionTarget.style.background = sectionBg;
    }
    if (this.hasBreakdownFormulaRowTarget) {
      this.breakdownFormulaRowTarget.style.background
        = tier === "minimum" ? "var(--bg)" : "var(--surface)";
    }
    if (this.hasTierBlockTarget) {
      this.tierBlockTargets.forEach((block) => {
        const active = block.dataset.tier === tier;
        block.style.boxShadow = active ? "inset 0 0 0 2px var(--accent)" : "";
      });
    }
    if (this.hasTierButtonTarget) {
      this.tierButtonTargets.forEach((button) => {
        const active = button.dataset.pilotageTjmTierParam === tier;
        button.style.background = active ? "var(--accent)" : "var(--bg)";
        button.style.color = active ? "#fff" : "";
        button.style.borderColor = active ? "var(--accent)" : "";
      });
    }
  }

  /**
   * Remplit une ligne de charge avec sa moyenne indicative (bouton
   * « ← N € »), puis déclenche le recalcul + sauvegarde automatique.
   */
  fillSuggestion(event) {
    const amount = Number(event?.params?.amount);
    if (!Number.isFinite(amount)) return;

    const input = event.currentTarget.parentElement?.querySelector(
      "input[data-pilotage-tjm-target='chargeInput']",
    );
    if (!input) return;

    input.value = this._formatEditableNumber(amount, 2);
    this.scheduleRecalc();
  }

  _setSaveStatus(text) {
    if (this.hasSaveStatusTarget) {
      this.saveStatusTarget.textContent = text;
    }
  }

  async _submit() {
    const payload = this._buildPayload();
    if (payload === null) return false;

    try {
      const response = await api.updatePilotageTjm(payload);
      this.profile = response.profile ?? this.profile;
      this.lastResults = response.results ?? this.lastResults;
      this.assumptions = response.assumptions ?? this.assumptions;
      this.realActivity = response.realActivity ?? this.realActivity;
      if (this.lastResults !== null) {
        this._renderTjmTiers(this.lastResults);
        this._renderBreakdown(this.visibleTier, this.lastResults);
      }
      this._renderAssumptions(this.assumptions);
      this._renderRealActivity(this.realActivity, this.lastResults);
      this._setSaveStatus("✓ Enregistré");

      return true;
    } catch (error) {
      console.warn("[pilotage-tjm] save failed", error);
      this._setSaveStatus("Échec de l'enregistrement");
      this.showError("Le recalcul a échoué. Vérifiez les valeurs saisies.");

      return false;
    }
  }

  _buildPayload() {
    if (this.profile === null) return null;

    const business = Number(this.businessDaysInputTarget?.value ?? 0) || 0;
    const vacation = Number(this.vacationDaysInputTarget?.value ?? 0) || 0;
    const nonBillable = Number(this.nonBillableInputTarget?.value ?? 0) || 0;
    const effective = Math.max(1, business - vacation);
    const unbilledRatio = Math.min(0.999, Math.max(0, nonBillable / effective));

    const tmiText = this.tmiSelectTarget?.options[this.tmiSelectTarget.selectedIndex]?.text ?? "";
    const tmiMatch = tmiText.match(/(\d+)\s*%/);
    const tmiPercent = tmiMatch ? Number(tmiMatch[1]) : 0;

    return {
      selectedActivityId: this.profile.selectedActivityId ?? null,
      desiredMonthlyIncome: this._parseLooseNumber(this.incomeInputTarget?.value ?? "0"),
      acreApplied: Boolean(this.profile.acreApplied),
      irVlOpted: Boolean(this.profile.irVlOpted),
      estimatedTmi: this.profile.irVlOpted ? null : Number((tmiPercent / 100).toFixed(4)),
      businessDaysPerYear: business,
      vacationDays: vacation,
      unbilledRatio: Number(unbilledRatio.toFixed(3)),
      fixedChargesMonthly: this._buildFixedChargesPayload(),
    };
  }

  _buildFixedChargesPayload() {
    const result = {
      notamicro: null,
      rcpro: null,
      decennale: null,
      sante: null,
      prevoyance: null,
      custom: [],
    };
    if (!this.hasChargeInputTarget) return result;

    const customs = [];
    this.chargeInputTargets.forEach((input) => {
      const key = input.dataset.pilotageTjmKeyParam ?? "";
      const mapping = CHARGE_KEYS[key];
      const raw = this._parseLooseNumber(input.value);
      const value = Number.isFinite(raw) ? raw : 0;
      if (mapping === undefined) return;
      if (typeof mapping === "string") {
        result[mapping] = value > 0 ? value : null;

        return;
      }
      customs.push({ label: mapping.label, amount: value });
    });
    result.custom = customs;

    return result;
  }

  _parseLooseNumber(value) {
    if (typeof value !== "string") return Number.NaN;
    const normalized = value.replace(/\s/g, "").replace(",", ".");
    const parsed = Number.parseFloat(normalized);

    return Number.isFinite(parsed) ? parsed : Number.NaN;
  }

  _formatEditableNumber(value, fractionDigits) {
    if (!Number.isFinite(value)) return "0";

    return new Intl.NumberFormat("fr-FR", {
      minimumFractionDigits: fractionDigits,
      maximumFractionDigits: fractionDigits,
      useGrouping: true,
    }).format(value);
  }
}
